import { useMemo } from 'react';

import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import {
  ACCESS_SRG,
  AgentStatus,
  AnalyticEvent,
  NO_AGENT_CONTRACT,
  SUBSCRIPTION_RESTRICTED,
  SUBSCRIPTION_UNAVAILABLE,
  UNABLE_TO_CONCLUDE_CONTRACT,
  UNILATERALLY_EXPIRED,
} from '@shared/constants';
import { useStores } from '@shared/hooks';
import { ProductCode } from '@shared/types';
import {
  PRODUCT_IMG_MAP,
  PRODUCT_LINK_MAP,
  addTestAttribute,
} from '@shared/utils';

import type { ProductCodeFiltered } from '../../model';
import { ProductItem } from '../product-item';
import { List, ProductLink } from './product-list.styles';

export const ProductList = observer(() => {
  const {
    MainStore: {
      agentUserStore: { availableSign, agent },
      productsStore: { products },
    },
  } = useStores();

  const shouldConcludeContract = useMemo(
    () =>
      !!availableSign?.visibilityDocuments &&
      !!availableSign?.isContractAvailable,
    [availableSign]
  );

  const isUnableToConcludeContract = useMemo(
    () =>
      availableSign?.visibilityDocuments === false &&
      availableSign?.isContractAvailable === false,
    [availableSign]
  );

  const isTerminationInProgress = useMemo(
    () =>
      availableSign?.visibilityDocuments === false &&
      !!availableSign?.contractTermination?.terminationStatusName,
    [availableSign]
  );

  const getLink = (productCode: ProductCodeFiltered) => {
    if (shouldConcludeContract) {
      return NO_AGENT_CONTRACT;
    }

    if (isTerminationInProgress) {
      return SUBSCRIPTION_UNAVAILABLE;
    }

    if (isUnableToConcludeContract) {
      return UNABLE_TO_CONCLUDE_CONTRACT;
    }

    if (agent?.status === AgentStatus.DENIED) {
      return SUBSCRIPTION_RESTRICTED;
    }

    if (availableSign?.isUnilaterallyExpired) {
      return UNILATERALLY_EXPIRED;
    }

    if (
      productCode === ProductCode.MORTGAGE &&
      availableSign?.isSrgRegistered === false
    ) {
      return ACCESS_SRG;
    }

    return PRODUCT_LINK_MAP[productCode];
  };

  return (
    !!products?.length && (
      <List>
        {products.map(({ productCode, productName, agentFeeDisplay }) => (
          <ProductLink
            onClick={() => {
              sendAnalyticEvent(AnalyticEvent.AGENT_NEWPRODUCT, {
                isagent:
                  shouldConcludeContract || isUnableToConcludeContract
                    ? 'n'
                    : 'y',
                product: productCode,
              });
            }}
            key={productName}
            to={getLink(productCode)}
            {...addTestAttribute(`products.${productCode}.createSubscription`)}
          >
            <ProductItem
              image={PRODUCT_IMG_MAP[productCode]}
              title={productName}
              badgeText={agentFeeDisplay}
            />
          </ProductLink>
        ))}
      </List>
    )
  );
});
